import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

/**
 * Option value for a product family option (e.g., "Red" for "Color")
 */
const ProductFamilyOptionValueSchema_ = z.object({
  id: z.string(),
  value: z.string(),
  position: z.number(),
});
export const ProductFamilyOptionValueSchema: z.ZodType<ProductFamilyOptionValue> =
  ProductFamilyOptionValueSchema_;
export interface ProductFamilyOptionValue extends z.infer<
  typeof ProductFamilyOptionValueSchema_
> {}

/**
 * Discriminator option for a product family (e.g., "Size", "Color")
 * Contains all possible values for this option.
 */
const ProductFamilyOptionSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  position: z.number(),
  values: z.array(ProductFamilyOptionValueSchema),
});
export const ProductFamilyOptionSchema: z.ZodType<ProductFamilyOption> =
  ProductFamilyOptionSchema_;
export interface ProductFamilyOption extends z.infer<
  typeof ProductFamilyOptionSchema_
> {}

/**
 * A product's selected option value (links product to a specific option value)
 */
const ProductOptionSchema_ = z.object({
  id: z.string(),
  optionId: z.string(),
  optionName: z.string(),
  value: z.string(),
});
export const ProductOptionSchema: z.ZodType<ProductOption> =
  ProductOptionSchema_;
export interface ProductOption extends z.infer<typeof ProductOptionSchema_> {}

const ProductImageSchema_ = z.object({
  id: z.string().optional(),
  tempId: z.string().optional(), // Frontend-only ID for new items
  url: z.string(),
  alt_text: z.string().optional(),
  position: z.number(),
});
export const ProductImageSchema: z.ZodType<ProductImage> = ProductImageSchema_;
export interface ProductImage extends z.infer<typeof ProductImageSchema_> {}

const PopulatedProductSchema_ = z.object({
  id: z.string().optional(),
  tempId: z.string().optional(), // Frontend-only ID for new items
  title: z.string(),
  description: z.string(),
  vendor: z.string().nullish(),
  price: z.number().nullish(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  barcodes: z.array(z.string()).default([]),
  options: z.array(ProductOptionSchema),
  quantity: z.number(),
  images: z.array(ProductImageSchema).default([]),
});

export const PopulatedProductSchema: z.ZodType<PopulatedProduct> =
  PopulatedProductSchema_;
export interface PopulatedProduct extends z.infer<
  typeof PopulatedProductSchema_
> {}

const PopulatedProductFamilySchema_ = z.object({
  id: z.string(),
  account_id: z.string(),
  legacy_account_id: z.string(),
  title: z.string(),
  description: z.string(),
  vendor: z.string().nullish(),
  products: z.array(PopulatedProductSchema),
  images: z.array(ProductImageSchema),
  options: z.array(ProductFamilyOptionSchema),
});

export const PopulatedProductFamilySchema: z.ZodType<PopulatedProductFamily> =
  PopulatedProductFamilySchema_;
export interface PopulatedProductFamily extends z.infer<
  typeof PopulatedProductFamilySchema_
> {}

export const getProductFamilyWithInventory = unaryRpc({
  name: "getProductFamilyWithInventory" as const,
  input: z
    .object({
      productFamilyId: z.string().nullish(),
      externalProductId: z.string().nullish(),
      sku: z.string().nullish(),
      locationId: z.string(),
    })
    .refine(
      (data) => data.productFamilyId || data.externalProductId || data.sku,
      {
        message:
          "Either productId or externalProductId or sku must be provided",
      },
    ),
  output: PopulatedProductFamilySchema.nullable(),
});
